import React from 'react'
import PropTypes from 'prop-types'
import { Button, Separator } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'

/**
 * ReviewCardFooter
 *
 * Renders the action buttons for the Review Card:
 *  - Normally: "Attach to Tree" + "Close", plus "Add Household Member" when a handler is given.
 *  - Close only: just "Close", for a panel whose caller owns what happens next.
 *
 * @component
 * @param {Object}   props
 * @param {Function} props.onSave                Called when the user clicks a save button, with 'close' or 'attach'.
 * @param {Function} [props.onAddAnotherPerson]  Called when the user clicks "Add Household Member".
 * @param {boolean}  [props.closeOnly=false]     Renders Close as the only way out.
 * @returns {JSX.Element}
 */
const ReviewCardFooter = ({ onSave, onAddAnotherPerson, closeOnly = false }) => {
  const [t] = useTranslation()

  return (
    <>
      {!closeOnly && (
        <>
          <Button
            emphasis="high"
            onClick={() => onSave('attach')}
            fullWidth
            data-testid="review-card-save-attach-button"
            linkName="Review Card Attach to Tree"
          >
            {t('review-card.footer.attach-to-tree', 'Attach to Tree')}
          </Button>
          <Separator size="xxs" />
          {onAddAnotherPerson && (
            <>
              <Button
                emphasis="medium"
                onClick={() => onAddAnotherPerson()}
                fullWidth
                data-testid="review-card-add-household-member-button"
                linkName="Review Card Add Household Member"
              >
                {t('review-card.footer.add-household-member', 'Add Household Member')}
              </Button>
              <Separator size="xxs" />
            </>
          )}
        </>
      )}
      <Button
        emphasis={closeOnly ? 'high' : 'medium'}
        onClick={() => onSave('close')}
        fullWidth
        data-testid="review-card-save-close-button"
        linkName="Review Card Close"
      >
        {t('common-ui:close.action', 'Close')}
      </Button>
    </>
  )
}

ReviewCardFooter.propTypes = {
  onSave: PropTypes.func.isRequired,
  onAddAnotherPerson: PropTypes.func,
  closeOnly: PropTypes.bool,
}

ReviewCardFooter.defaultProps = {
  closeOnly: false,
}

export default ReviewCardFooter
